import { useCallback } from 'react'
import { Link } from 'react-router'

import { DailyStatusBody } from '../../components/daily/DailyStatusView'
import { JournalSummary } from '../../components/daily/JournalSummary'
import { PairCard } from '../../components/daily/PairCard'
import { PageHeader } from '../../components/PageHeader'
import { Card, Icon, Notice, ProblemNotice, RecordingStatusPill } from '../../components/ui'
import { useAuth } from '../../contexts/AuthContext'
import { getDailyStatus, listRecordings, type RecordingRecord } from '../../lib/api'
import { useRetryLanding, type ArmLanding } from '../../lib/useRetryLanding'
import { readDailyStatus, type DailyStatusResult } from '../../lib/dailyStatus'
import { deviceLabel } from '../../lib/devices'
import { PAIRED_SESSION_ENABLED } from '../../lib/features'
import { formatSessionDate } from '../../lib/format'
import { PAIR_LEAD, pairLinkOf } from '../../lib/pair'
import { CONDITION_WORDS, RECORDING_OUTCOME, bySessionDate } from '../../lib/recordings'
import type { Page } from '../../lib/usePagedList'
import { usePagedList } from '../../lib/usePagedList'
import { pickFromPages, type PickState } from '../../lib/usePickFromPages'
import { useResource } from '../../lib/useResource'
import { usePublishAssistantPage } from '../../contexts/AssistantContext'

/**
 * Today — the most recent scored recording, read back from the server.
 *
 * ── THE THING THIS SCREEN GETS WRONG IF IT IS WRITTEN QUICKLY ───────────────
 *
 * `items[0]` is the wrong recording. `GET /users/{id}/recordings` pages newest
 * first by **`created_at`**; §1.1 asks for the most recent recording by its
 * **actual recording date**, which is `session_date`. A window derived with
 * `scripts/derive_session_window.py`, a backfilled upload, or anything recorded
 * offline and sent later makes those two orderings disagree — so today's result
 * is not necessarily on page one, and the newest row is not necessarily today's.
 *
 * So the screen reads a bounded number of pages and takes the `session_date`
 * maximum among the `scored` rows, and it says which bound it hit: "nothing
 * scored in your history" and "nothing scored in the pages read so far" are
 * different sentences, and collapsing them would let a partial scan claim there
 * is nothing to show (§4.5).
 *
 * ── AND THE SECOND HALF OF §1.1 ─────────────────────────────────────────────
 *
 * Picking the newest *scored* result can hide an unsuccessful attempt made after
 * it. The latest row by `created_at` is therefore checked separately, and shown
 * as its own notice when it is not the one being rendered.
 *
 * Everything scientific on this page comes from `DailyStatusBody`, which renders
 * the payload. This file chooses *which* recording and nothing else about it.
 */

/**
 * How many pages Today will read on its own looking for a scored recording.
 *
 * Three pages is 150 recordings at the default page size — months of daily use.
 * A budget rather than "read until found", because a maximum over an unsorted
 * key cannot stop early: without a bound, a user with a long history and no
 * scored recording would page their whole archive on every visit to this screen.
 */
export const TODAY_PAGE_BUDGET = 3

/** How many outcomes the "nothing scored" state lists before it says so. */
const OUTCOMES_SHOWN = 5

export function Today() {
  const { account } = useAuth()
  const userId = account?.user_id ?? null

  // Total rather than conditional: hooks cannot be called behind an `if`, and
  // this screen only renders inside RequireAuth, so the null branch is a type
  // obligation rather than a state a user reaches.
  const fetchPage = useCallback(
    (cursor: string | null, signal: AbortSignal): Promise<Page<RecordingRecord>> =>
      userId === null
        ? Promise.resolve({ items: [], next_cursor: null })
        : listRecordings(userId, { cursor }, { signal }),
    [userId],
  )

  const list = usePagedList<RecordingRecord>(fetchPage, { autoLoadPages: TODAY_PAGE_BUDGET })
  const pick = pickFromPages(list, (row) => row.status === 'scored', bySessionDate)
  const picked = pick.kind === 'picked' ? pick.item : null
  // agent-1-web: the assistant reads THIS recording, and its chip says which.
  // A no-op without the assistant.
  usePublishAssistantPage(
    picked === null
      ? null
      : { recordingId: picked.recording_id, condition: picked.condition, dateLabel: formatSessionDate(picked.session_date) },
  )

  const loadStatus = useCallback(
    (signal: AbortSignal) =>
      picked === null
        ? Promise.resolve(null)
        : getDailyStatus(picked.recording_id, { signal }),
    [picked],
  )
  const status = useResource(loadStatus)
  // One landing for both retries on this screen; see the wrapper below.
  const [landOn, armLanding] = useRetryLanding(list.error != null || status.error != null)
  const result: DailyStatusResult | null =
    status.data === null ? null : readDailyStatus(status.data)

  // §1.1's second half, read for what it is FOR.
  //
  // The clause says to show the latest recording's outcome separately "so
  // selecting the last scored result does not hide an unsuccessful attempt".
  // Taken as `items[0]`, that misses the case it exists to prevent: with rows
  // [scored (uploaded today), failed, scored (the pick)], the newest row is
  // successful, so nothing is reported and the failure between them is invisible
  // — which is precisely an unsuccessful attempt hidden behind a scored result.
  //
  // So the question is not "what is the newest row" but "did anything fail after
  // the result I am being shown". Later by `session_date`, the same axis the
  // result itself is chosen on, because that is what a user means by "since".
  const unsuccessfulSincePick =
    picked === null
      ? []
      : list.items
          .filter((r) => r.status !== 'scored' && r.session_date > picked.session_date)
          .sort(bySessionDate)
  // Newest last, so the end of the sorted list is the one to name.
  const latestFailure = unsuccessfulSincePick.at(-1) ?? null

  return (
    <div className="stack stack--wide">
      {/* The eyebrow identifies this result by its own recording facts.
          The date is `formatSessionDate`, not `formatDate`: `session_date` is
          a plain calendar date, and this suite runs in America/New_York, where
          `new Date("2026-06-24")` is UTC midnight and renders as the 23rd. */}
      <PageHeader
        title="Report"
        eyebrow={
          picked === null
            ? 'Daily status'
            : // The headband last, and only when this build can name it: the
              // resolved device is a fact about the recording like its
              // condition and date (Sprint 1.9.2 S4). One text node still.
              [
                'Daily status',
                CONDITION_WORDS[picked.condition],
                formatSessionDate(picked.session_date),
                deviceLabel(picked.device_id),
              ].filter((part): part is string => part !== null).join(' · ')
        }
        actions={
          picked !== null && (
            <Link className="btn btn--secondary btn--sm daily-record-action" to="/record">
              <Icon name="record" />
              Record a session
            </Link>
          )
        }
      />

      {/* A read that failed PART WAY does not discard what it read. The pages
          that arrived are still real recordings, and if one of them is scored it
          is still this user's latest result -- hiding it behind the error would
          throw away an answer we have in order to report a question we could not
          finish asking. So the failure is reported and the body still renders,
          with `exhaustive: false` doing the rest of the honesty. */}
      {list.error != null && (
        <ProblemNotice error={list.error} onRetry={list.reload} landing={armLanding}>
          Some of your history could not be loaded, so this may not be complete.
        </ProblemNotice>
      )}

      {/* WHERE FOCUS GOES WHEN EITHER RETRY ON THIS SCREEN WORKS.
          Both notices unmount when their error clears, taking the focused "Try
          again" with them -- the case `Button.tsx:67-69` names as uncoverable.
          This body is what now says what happened, for the list read and for
          the result read alike, which is why one landing serves both. A plain
          `<div>` inside `.stack`, a flex column with a gap (product.css:31-36),
          so the spacing is unchanged. */}
      <div className="retry-landing subgrid" ref={landOn} tabIndex={-1}>
        {list.isLoading ? (
          <Card>
            <p className="loading-row" role="status">
              <span className="spinner" aria-hidden="true" /> Looking for your latest result…
            </p>
          </Card>
        ) : (
          <>
            {latestFailure !== null && (
              <LatestOutcomeNotice
                recording={latestFailure}
                alsoHidden={unsuccessfulSincePick.length - 1}
              />
            )}
            <TodayBody
              pick={pick}
              rows={list.items}
              result={result}
              isLoading={status.isLoading}
              error={status.error}
              onRetry={status.reload}
              landing={armLanding}
            />
          </>
        )}
      </div>
    </div>
  )
}

/**
 * Every `PickState`, with no hole.
 *
 * Written as a `switch` on purpose. The first version of this screen was a run of
 * `&&` expressions, one per state it expected -- and `scanning` was not among
 * them, so a list that finished loading without either completing or spending its
 * budget rendered **nothing at all**. That combination is unreachable at today's
 * page budget and is one edit away from being reachable: drop `autoLoadPages` to
 * 1 and `budgetSpent` is never set, or let the list error on page two and the
 * loop exits with neither flag. A blank screen is the worst of the available
 * failures because it looks like a screen that has nothing to say.
 */
function TodayBody({
  pick,
  rows,
  result,
  isLoading,
  error,
  onRetry,
  landing,
}: {
  pick: PickState<RecordingRecord>
  rows: readonly RecordingRecord[]
  result: DailyStatusResult | null
  isLoading: boolean
  error: unknown
  onRetry: () => void
  landing?: ArmLanding
}) {
  switch (pick.kind) {
    case 'picked':
      return (
        <DailyResult
          recording={pick.item}
          result={result}
          isLoading={isLoading}
          error={error}
          onRetry={onRetry}
          exhaustive={pick.exhaustive}
          landing={landing}
        />
      )
    case 'absent':
      // Read to the end. Either there is nothing at all, or nothing scored.
      return rows.length === 0 ? <NoRecordingsYet /> : <NothingScored rows={rows} exhaustive />
    case 'capped':
    case 'scanning':
      // Pages remain unread, for a budget or for a failure. Either way the
      // sentence is the same one and it is NOT "you have nothing".
      return <NothingScored rows={rows} exhaustive={false} />
  }
}

/**
 * The result itself, or an honest reason there is none to show.
 *
 * §1.7: "an unusable payload gets a clear unavailable state rather than scores".
 * A half-rendered readout is the thing that reads as a measurement, so the
 * unreadable branch renders no card at all.
 */
function DailyResult({
  recording,
  result,
  isLoading,
  error,
  onRetry,
  exhaustive,
  landing,
}: {
  /** The ROW the result belongs to. The payload carries no condition and no device. */
  recording: RecordingRecord
  result: DailyStatusResult | null
  isLoading: boolean
  error: unknown
  onRetry: () => void
  exhaustive: boolean
  landing?: ArmLanding
}) {
  if (isLoading) {
    return (
      <Card>
        <p className="loading-row" role="status">
          <span className="spinner" aria-hidden="true" /> Loading your result…
        </p>
      </Card>
    )
  }

  if (error != null) {
    return (
      <ProblemNotice error={error} onRetry={onRetry} landing={landing}>
        This result could not be loaded.
      </ProblemNotice>
    )
  }

  if (result === null) return null

  if (!result.readable) {
    return <Notice tone="warn">{result.detail}</Notice>
  }

  // The paired session (Sprint 1.9.13 W4, concept D): one sentence in the lead
  // and the Alpha reactivity card after the rows. A plain recording, and every
  // recording with the flag off, shows neither; the daily reading is unchanged.
  const pair = PAIRED_SESSION_ENABLED ? pairLinkOf(recording) : null

  return (
    <>
      {/* Shown only while pages remain unread, and phrased as what it is: the
          latest one FOUND, not the latest one there is. */}
      {!exhaustive && (
        <p className="page__lead">
          This is the most recent result in the pages read so far. Older pages of your
          history have not been searched.
        </p>
      )}
      {pair !== null && <p className="page__lead">{PAIR_LEAD[pair.pairRole]}</p>}
      <DailyStatusBody
        status={result}
        recording={{ condition: recording.condition, deviceId: recording.device_id }}
        afterRows={
          <>
            <JournalSummary recording={recording} />
            {pair !== null && <PairCard recordingId={recording.recording_id} pairRole={pair.pairRole} />}
          </>
        }
      />
    </>
  )
}

/**
 * §1.1's second half — the attempt made after the result being shown.
 *
 * Without this, a recording rejected this morning is invisible behind
 * yesterday's good one, and the user is left believing their most recent attempt
 * worked.
 */
function LatestOutcomeNotice({
  recording,
  alsoHidden,
}: {
  recording: RecordingRecord
  alsoHidden: number
}) {
  const outcome = RECORDING_OUTCOME[recording.status]
  return (
    <Notice tone={outcome?.isError ? 'error' : 'warn'} className="outcome-notice">
      <p className="confidence__headline">You have recorded since this result.</p>
      <p>
        <RecordingStatusPill status={recording.status} />{' '}
        <span className="outcome-date">· {formatSessionDate(recording.session_date)}</span>
      </p>
      {outcome?.detail != null && <p>{outcome.detail}</p>}
      {outcome === undefined && (
        <p>
          This app does not recognise the state <code className="mono-cell">{recording.status}</code>.
        </p>
      )}
      {alsoHidden > 0 && (
        <p>
          {alsoHidden} earlier recording{alsoHidden === 1 ? '' : 's'} since this result did not
          produce one either.
        </p>
      )}
    </Notice>
  )
}

/** §1.6 — no recordings at all, routed to Record. */
function NoRecordingsYet() {
  return (
    <Card>
      {/* `.empty` is the S1 primitive; `.product-empty__title` and
          `.product-empty__body` stay on the title and the body because five
          screens share them and the block is frozen after S1. */}
      <div className="empty">
        <img className="empty__mark" src="/mark-small.svg" alt="" />
        <h2 className="product-empty__title">No recordings yet</h2>
        <p className="product-empty__body">
          Your Daily Status appears here once a session has been uploaded and scored.
          Nothing is measured until you record something.
        </p>
        <Link className="btn btn--primary" to="/record">
          <Icon name="record" />
          Record a session
        </Link>
      </div>
    </Card>
  )
}

/**
 * §1.7 — "if history exists but none is scored, show those outcomes and a route
 * to Record".
 *
 * `exhaustive` is the difference between *"none of your recordings produced a
 * result"* and *"none of the ones read so far did"*. §4.5 forbids collapsing
 * them, and the wording below is the only place that distinction becomes
 * visible to a user.
 */
function NothingScored({
  rows,
  exhaustive,
}: {
  rows: readonly RecordingRecord[]
  exhaustive: boolean
}) {
  return (
    <Card>
      <div className="empty empty--start">
        <h2 className="product-empty__title">No scored result yet</h2>
        <p className="product-empty__body">
          {exhaustive
            ? `You have ${rows.length} recording${rows.length === 1 ? '' : 's'}, and none of them produced a result.`
            : // "most recently UPLOADED", not "most recent". The list is ordered
              // by `created_at`, and the whole reason this screen picks by
              // `session_date` is that the two disagree -- so the one sentence
              // that describes the scan must not quietly conflate them again.
              `None of the ${rows.length} most recently uploaded recordings produced a result. Older pages of your history have not been searched.`}
        </p>
        <ul className="validity-note__list" role="list">
          {rows.slice(0, OUTCOMES_SHOWN).map((row) => (
            <li key={row.recording_id}>
              <RecordingStatusPill status={row.status} />{' '}
              <span className="outcome-date">· {formatSessionDate(row.session_date)}</span>
            </li>
          ))}
        </ul>
        {/* Truncation that says so. A list silently cut at five reads as the
            whole of a short history, which is the §4.5 failure in miniature. */}
        {rows.length > OUTCOMES_SHOWN && (
          <p className="product-empty__body">
            Showing {OUTCOMES_SHOWN} of {rows.length}.
          </p>
        )}
        <Link className="btn btn--primary" to="/record">
          <Icon name="record" />
          Record a session
        </Link>
      </div>
    </Card>
  )
}
